import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useLang, loc, UI, EXAM_PARTS } from "@/lib/content";

export default function ExamSection() {
  const { lang } = useLang();
  return (
    <section id="examen" className="bg-paper py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage">
            <span className="h-px w-8 bg-heritage" /> {loc(UI.examHeader.overline, lang)}
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
            {loc(UI.examHeader.title, lang)}
            <span className="italic text-heritage">{loc(UI.examHeader.titleAccent, lang)}</span>
          </h2>
          <p className="mt-4 text-inkbody">{loc(UI.examHeader.intro, lang)}</p>
        </motion.div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {EXAM_PARTS.map((part, i) => (
            <motion.article
              key={part.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`exam-card-${i + 1}`}
              className="group relative flex flex-col rounded-3xl border border-line bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
            >
              <span className="font-mono text-xs font-semibold tracking-[0.2em] text-heritage">{part.num}</span>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display text-6xl font-semibold leading-none text-navy">{part.big}</span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-mutedtext">{loc(part.unit, lang)}</span>
              </div>
              <h3 className="mt-4 text-xl font-bold text-navy">{loc(part.title, lang)}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-inkbody">{loc(part.desc, lang)}</p>
              <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-emeraldsucc/10 px-4 py-2 text-xs font-bold text-emeraldsucc">
                <CheckCircle2 size={14} /> {loc(part.ok, lang)}
              </p>
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 border-l-2 border-brass pl-5 text-sm italic text-mutedtext"
        >
          {loc(UI.examHeader.note, lang)}
        </motion.p>
      </div>
    </section>
  );
}
